import { useCallback, useEffect, useMemo } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { useAuthStore } from "@app/store/useAuthStore";
import { useUserStore } from "@app/store/useUserStore";
import { pickFormSubmissionMemoryFields } from "./formSubmissionMemoryHelpers";
import {
    cleanupLegacyFormSubmissionMemoryNamespaces,
    readFormSubmissionMemoryValues,
    writeFormSubmissionMemoryValues,
} from "./formSubmissionMemoryStorage";
import type { FormSubmissionMemoryIdentity, FormSubmissionMemoryOptions } from "./types";
import { wrapFormSubmissionMemorySubmitHandler } from "./wrapFormSubmissionMemorySubmitHandler";

/**
 * App-level hook для restore/save submission memory в {@link BusinessForm}.
 *
 * @template TFieldValues тип значений формы
 * @param options конфигурация memory или `undefined`
 * @param allowRestore разрешает чтение remembered values для текущего сценария инициализации
 * @returns remembered values и helpers для save после успешного submit
 */
export function useFormSubmissionMemory<TFieldValues extends FieldValues>(
    options: FormSubmissionMemoryOptions<TFieldValues> | undefined,
    allowRestore = true,
) {
    const serverUrl = useAuthStore((state) => state.server?.url ?? null);
    const userCode = useUserStore((state) => state.currentUser?.Code ?? null);
    const runtimeReady = serverUrl != null && serverUrl.length > 0 && userCode != null;
    const key = options?.key;
    const scope = options?.scope;
    const schemaVersion = options?.schemaVersion;
    const fieldAllowlist = options?.fields;

    const identity = useMemo<FormSubmissionMemoryIdentity | null>(() => {
        if (!runtimeReady || key == null || schemaVersion == null) {
            return null;
        }

        return {
            key,
            scope,
            schemaVersion,
        };
    }, [key, runtimeReady, schemaVersion, scope]);

    const runtime = useMemo(
        () => ({
            serverUrl,
            userCode,
        }),
        [serverUrl, userCode],
    );

    const shouldRestore = allowRestore && options?.restore !== false;

    useEffect(() => {
        cleanupLegacyFormSubmissionMemoryNamespaces();
    }, []);

    const rememberedValues = useMemo((): Partial<TFieldValues> | null => {
        if (identity == null || !shouldRestore) {
            return null;
        }

        const raw = readFormSubmissionMemoryValues(identity, runtime, fieldAllowlist);
        if (raw == null) {
            return null;
        }

        // TODO(types): remove assertion after storage options require a runtime parser for persisted field values.
        // JSON parsing can validate the envelope and field names, but cannot prove generic field value types.
        return raw as Partial<TFieldValues>;
    }, [fieldAllowlist, identity, runtime, shouldRestore]);

    const saveSubmittedValues = useCallback(
        (values: TFieldValues) => {
            if (identity == null) {
                return;
            }

            const payload = pickFormSubmissionMemoryFields(values, fieldAllowlist);
            writeFormSubmissionMemoryValues(identity, runtime, payload);
        },
        [fieldAllowlist, identity, runtime],
    );

    const wrapSubmitHandler = useCallback(
        (handler: SubmitHandler<TFieldValues> | undefined) =>
            wrapFormSubmissionMemorySubmitHandler(handler, saveSubmittedValues, identity != null),
        [identity, saveSubmittedValues],
    );

    return {
        rememberedValues,
        saveSubmittedValues,
        wrapSubmitHandler,
        isEnabled: identity != null,
    };
}
